/**
 * InputManager.ts — Unified input system supporting three simultaneous input methods.
 *
 * 1. Number entry: type a digit + Enter to select
 * 2. Arrow keys: Up/Down to highlight, Enter to confirm, Escape to cancel
 * 3. Mouse: click to select, hover to highlight, scroll for long lists
 *
 * All three methods emit the same event types so the MenuSystem doesn't
 * care which input method the user chose.
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

import type { Terminal } from './Terminal';
import type { InputWatchdog } from './InputWatchdog';

// --- Event Types ---

export type InputEventType =
  | 'menuSelect'    // User selected a menu option (by number, arrow+enter, or click)
  | 'textSubmit'    // User submitted text input (Enter after typing)
  | 'cancel'        // User pressed Escape or selected 0 (back/exit)
  | 'highlightMove' // Arrow key or mouse hover moved the highlight
  | 'scrollUp'      // PageUp or scroll wheel up
  | 'scrollDown'    // PageDown or scroll wheel down
  | 'anyKey'        // Any key was pressed (for "press any key to continue")
  | 'hotkey';       // Special hotkey pressed (Tab, ?, c, etc.)

export interface InputEvent {
  type: InputEventType;
  /** The selected menu index (0-based) for menuSelect events */
  index?: number;
  /** The entered number for number-entry selection */
  number?: number;
  /** The submitted text for textSubmit events */
  text?: string;
  /** Direction of highlight movement */
  direction?: 'up' | 'down' | 'home' | 'end' | 'left' | 'right';
  /** Raw key string */
  key?: string;
  /** Mouse coordinates (terminal row/col, 1-based) */
  mouseRow?: number;
  mouseCol?: number;
}

export type InputHandler = (event: InputEvent) => void;

// --- Input Modes ---

export type InputMode =
  | 'menu'       // Normal menu navigation (numbers, arrows, mouse all active)
  | 'text'       // Free text entry (typing a name, etc.)
  | 'anyKey'     // "Press any key to continue"
  | 'disabled';  // Input suppressed (during animations, transitions)

// --- InputManager ---

export class InputManager {
  private terminal: Terminal;
  private handlers: InputHandler[] = [];
  private mode: InputMode = 'menu';
  private textBuffer = '';
  private highlightIndex = 0;
  private menuItemCount = 0;
  private numberBuffer = '';
  private numberBufferTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: InputWatchdog | null = null;

  constructor(terminal: Terminal) {
    this.terminal = terminal;
    this.setupKeyboardInput();
    this.setupMouseInput();
  }

  // --- Mode Control ---

  /** Set the current input mode */
  setMode(mode: InputMode): void {
    this.mode = mode;
    this.textBuffer = '';
    this.clearNumberBuffer();
  }

  getMode(): InputMode {
    return this.mode;
  }

  /** Set the number of menu items (for arrow key bounds) */
  setMenuItemCount(count: number): void {
    this.menuItemCount = count;
    if (this.highlightIndex >= count) {
      this.highlightIndex = Math.max(0, count - 1);
    }
  }

  /** Set the current highlight index (for syncing with menu state) */
  setHighlightIndex(index: number): void {
    this.highlightIndex = index;
  }

  getHighlightIndex(): number {
    return this.highlightIndex;
  }

  /** Attach an InputWatchdog for stuck-state detection. */
  setWatchdog(watchdog: InputWatchdog): void {
    this.watchdog = watchdog;
  }

  // --- Event Subscription ---

  /** Subscribe to input events */
  on(handler: InputHandler): void {
    this.handlers.push(handler);
  }

  /** Unsubscribe from input events */
  off(handler: InputHandler): void {
    this.handlers = this.handlers.filter(h => h !== handler);
  }

  /** Remove all handlers */
  clearHandlers(): void {
    this.handlers = [];
  }

  /** Emit an event to all handlers */
  private emit(event: InputEvent): void {
    // Notify watchdog of valid input for stuck-state detection
    if (this.watchdog && (
      event.type === 'menuSelect' ||
      event.type === 'textSubmit' ||
      event.type === 'cancel' ||
      event.type === 'anyKey'
    )) {
      this.watchdog.onValidInput();
    }
    for (const handler of this.handlers) {
      handler(event);
    }
  }

  /** Inject a synthetic event from external code (e.g., canvas click handlers). */
  injectEvent(event: InputEvent): void {
    this.emit(event);
  }

  // --- Keyboard Input Setup ---

  private setupKeyboardInput(): void {
    this.terminal.onKey((key: string, domEvent: KeyboardEvent) => {
      // Notify watchdog of raw keypress for stuck-state detection
      if (this.watchdog) this.watchdog.onKeypress();

      // Global font size shortcuts — work in any mode
      if (domEvent.ctrlKey && (domEvent.key === '=' || domEvent.key === '+')) {
        domEvent.preventDefault();
        const current = this.terminal.getFontSize();
        if (current < 28) this.terminal.setFontSize(current + 2);
        return;
      }
      if (domEvent.ctrlKey && domEvent.key === '-') {
        domEvent.preventDefault();
        const current = this.terminal.getFontSize();
        if (current > 8) this.terminal.setFontSize(current - 2);
        return;
      }

      if (this.mode === 'disabled') return;

      if (this.mode === 'anyKey') {
        this.emit({ type: 'anyKey', key });
        return;
      }

      if (this.mode === 'text') {
        this.handleTextInput(key, domEvent);
        return;
      }

      if (this.mode === 'menu') {
        this.handleMenuInput(key, domEvent);
        return;
      }
    });
  }

  private handleTextInput(key: string, domEvent: KeyboardEvent): void {
    const code = domEvent.key;

    if (code === 'Enter') {
      this.terminal.writeRaw('\r\n');
      this.emit({ type: 'textSubmit', text: this.textBuffer });
      this.textBuffer = '';
    } else if (code === 'Escape') {
      this.textBuffer = '';
      this.emit({ type: 'cancel' });
    } else if (code === 'Backspace') {
      if (this.textBuffer.length > 0) {
        this.textBuffer = this.textBuffer.slice(0, -1);
        this.terminal.writeRaw('\b \b');
      }
    } else if (key.length === 1 && !domEvent.ctrlKey && !domEvent.altKey) {
      this.textBuffer += key;
      this.terminal.write(key, 'icyWhite');
    }
  }

  private handleMenuInput(key: string, domEvent: KeyboardEvent): void {
    const code = domEvent.key;

    // Arrow keys
    if (code === 'ArrowUp') {
      domEvent.preventDefault();
      this.numberBuffer = '';
      if (this.highlightIndex > 0) {
        this.highlightIndex--;
        this.emit({ type: 'highlightMove', direction: 'up', index: this.highlightIndex });
      }
      return;
    }

    if (code === 'ArrowDown') {
      domEvent.preventDefault();
      this.numberBuffer = '';
      if (this.highlightIndex < this.menuItemCount - 1) {
        this.highlightIndex++;
        this.emit({ type: 'highlightMove', direction: 'down', index: this.highlightIndex });
      }
      return;
    }

    if (code === 'Home') {
      domEvent.preventDefault();
      this.numberBuffer = '';
      this.highlightIndex = 0;
      this.emit({ type: 'highlightMove', direction: 'home', index: this.highlightIndex });
      return;
    }

    if (code === 'End') {
      domEvent.preventDefault();
      this.numberBuffer = '';
      this.highlightIndex = this.menuItemCount - 1;
      this.emit({ type: 'highlightMove', direction: 'end', index: this.highlightIndex });
      return;
    }

    if (code === 'PageUp') {
      domEvent.preventDefault();
      this.emit({ type: 'scrollUp' });
      return;
    }

    if (code === 'PageDown') {
      domEvent.preventDefault();
      this.emit({ type: 'scrollDown' });
      return;
    }

    // Escape
    if (code === 'Escape') {
      this.numberBuffer = '';
      this.emit({ type: 'cancel' });
      return;
    }

    // Enter
    if (code === 'Enter') {
      if (this.numberBuffer.length > 0) {
        const num = parseInt(this.numberBuffer, 10);
        this.clearNumberBuffer();
        if (num === 0) {
          this.emit({ type: 'cancel', number: 0 });
        } else {
          this.emit({ type: 'menuSelect', number: num, index: num - 1 });
        }
      } else {
        this.emit({ type: 'menuSelect', index: this.highlightIndex });
      }
      return;
    }

    // Backspace (number entry)
    if (code === 'Backspace') {
      this.numberBuffer = this.numberBuffer.slice(0, -1);
      return;
    }

    // Digit keys (number entry)
    if (key >= '0' && key <= '9') {
      const digit = parseInt(key, 10);

      // 0 always means cancel/back
      if (digit === 0 && this.numberBuffer.length === 0) {
        this.clearNumberBuffer();
        this.emit({ type: 'cancel', number: 0 });
        return;
      }

      // Check if this digit can be immediately selected:
      // It's unambiguous if no valid two-digit number starts with this digit.
      // e.g., pressing "2" with 12 items: 2*10=20 > 12, so "2" can't be start of 20+.
      // pressing "1" with 12 items: 1*10=10 <= 12, so "1" could mean 10,11,12 — buffer it.
      const wouldBeNum = this.numberBuffer.length === 0 ? digit : parseInt(this.numberBuffer + key, 10);
      const couldContinue = wouldBeNum * 10 <= this.menuItemCount;

      if (!couldContinue && wouldBeNum >= 1 && wouldBeNum <= this.menuItemCount) {
        // Unambiguous and valid — select immediately
        this.clearNumberBuffer();
        this.emit({ type: 'menuSelect', number: wouldBeNum, index: wouldBeNum - 1 });
        return;
      }

      // Ambiguous or building a multi-digit number — buffer it and start auto-submit timer
      this.numberBuffer += key;
      this.startNumberBufferTimer();
      return;
    }

    // Hotkeys (Tab, ?, c, C)
    if (code === 'Tab' || key === '?' || key === 'c' || key === 'C') {
      domEvent.preventDefault();
      this.emit({ type: 'hotkey', key: code === 'Tab' ? 'Tab' : key });
      return;
    }
  }

  // --- Mouse Input Setup ---

  private setupMouseInput(): void {
    this.terminal.enableMouseTracking();

    this.terminal.onData((data: string) => {
      if (this.mode !== 'menu') return;

      const match = data.match(/\x1b\[<(\d+);(\d+);(\d+)([Mm])/);
      if (!match) return;

      const button = parseInt(match[1], 10);
      const col = parseInt(match[2], 10);
      const row = parseInt(match[3], 10);
      const isRelease = match[4] === 'm';

      if (button === 0 && isRelease) {
        this.emit({ type: 'menuSelect', mouseRow: row, mouseCol: col });
        return;
      }

      if (button === 35) {
        this.emit({ type: 'highlightMove', mouseRow: row, mouseCol: col });
        return;
      }

      if (button === 64) {
        this.emit({ type: 'scrollUp' });
        return;
      }

      if (button === 65) {
        this.emit({ type: 'scrollDown' });
        return;
      }
    });
  }

  // --- Cleanup ---

  /**
   * Clear the number buffer and cancel any pending auto-submit timer.
   */
  private clearNumberBuffer(): void {
    this.numberBuffer = '';
    if (this.numberBufferTimer) {
      clearTimeout(this.numberBufferTimer);
      this.numberBufferTimer = null;
    }
  }

  /**
   * Start (or restart) the auto-submit timer for buffered digits (600ms delay).
   * If the user types multiple digits quickly, this allows building multi-digit
   * numbers. After 600ms of no additional input, the buffered number is submitted.
   */
  private startNumberBufferTimer(): void {
    if (this.numberBufferTimer) clearTimeout(this.numberBufferTimer);
    this.numberBufferTimer = setTimeout(() => {
      this.numberBufferTimer = null;
      if (this.numberBuffer.length > 0) {
        const num = parseInt(this.numberBuffer, 10);
        this.numberBuffer = '';
        if (num === 0) {
          this.emit({ type: 'cancel', number: 0 });
        } else if (num >= 1 && num <= this.menuItemCount) {
          this.emit({ type: 'menuSelect', number: num, index: num - 1 });
        }
        // If num > menuItemCount, silently discard (invalid input)
      }
    }, 600);
  }

  /**
   * Drain pending input events to prevent keystroke cascade (RK-073).
   * Temporarily disables input mode for one frame to let xterm's event loop flush.
   */
  async drain(): Promise<void> {
    this.clearNumberBuffer();
    const prevMode = this.mode;
    this.mode = 'disabled';
    // Wait one frame to let any pending events flush through xterm's event loop
    await new Promise(resolve => requestAnimationFrame(resolve));
    this.mode = prevMode;
  }

  dispose(): void {
    this.clearNumberBuffer();
    this.clearHandlers();
    this.terminal.disableMouseTracking();
  }
}
